import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ensureBibleHydrated, ensureBookAvailable } from '@/lib/bible/active';
import {
  buildScriptureAnchor,
  singleVerseFromFrozenAnchor,
} from '@/lib/scripture-anchor';
import { notesCoveringVerse } from '@/lib/bookmark-utils';
import type { ScriptureAnchor } from '@/types/prayer';
import { BIBLE_BOOKMARKS_ENABLED } from '@/lib/feature-flags';
import BibleBookmarkSheet from '@/components/BibleBookmarkSheet/BibleBookmarkSheet';
import BibleThemePicker from '@/components/BibleThemePicker/BibleThemePicker';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import JournalSourceState from '@/components/JournalSourceState/JournalSourceState';
import { useJournalSources } from '@/hooks/useJournalSources';
import { useSettingsStore } from '@/lib/settings-store';
import styles from './ScriptureReflectionPage.module.css';

/**
 * Scripture Reflection route at /bible/bookmarks/:bookSlug/:chapter/:verse
 * (Bible Bookmarks Phase 4, §9). A leaf route (rule #10; NO NavStack layer,
 * the route world has native browser back). The verse is presented warmly,
 * then the SHARED bookmark thread renders in 'page' presentation (§5): the
 * full thread for that verse + an add-a-reflection composer (read / add /
 * edit / delete, voice supported). A prominent "Read in full context" jumps
 * into the reader at the verse (the shipped ?v= mechanism, no reader change).
 *
 * Verse-covering notes only (notesCoveringVerse); whole-chapter (verses: [])
 * notes live in the journal's legacy row (§8.2) and never route here.
 */

// Strict positive integer (rule #71); excludes 0, so a whole-chapter sentinel
// can never reach this route.
const CHAPTER_PATTERN = /^[1-9]\d*$/;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; anchor: ScriptureAnchor }
  | { kind: 'missing' }
  | { kind: 'failed'; offline: boolean };

export default function ScriptureReflectionPage() {
  const { bookSlug = '', chapter = '', verse = '' } = useParams();
  // A real passage change owns a new editor and cancels the old load. Saved
  // notes refreshing within that passage must not replace its live composer.
  return (
    <ScriptureReflectionContent
      key={`${bookSlug}/${chapter}/${verse}`}
      bookSlug={bookSlug}
      chapter={chapter}
      verse={verse}
    />
  );
}

function ScriptureReflectionContent({ bookSlug, chapter, verse }: {
  bookSlug: string;
  chapter: string;
  verse: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bibleTheme = useSettingsStore((s) => s.bibleTheme);
  // App notice stack (v0.2.0 finishing): an in-flow spacer at the top of the
  // page (above the Back link and the theme picker, so the picker's menu also
  // opens below the notices) keeps the page below the fixed notices at the
  // top; 0 while no notice shows. Each render path mounts its own spacer with
  // the same callback ref.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();
  const { sources, state: sourceState, ready: sourcesReady, retry: retrySources } =
    useJournalSources();

  const inputsValid =
    BIBLE_BOOKMARKS_ENABLED &&
    !!bookSlug &&
    CHAPTER_PATTERN.test(chapter) &&
    CHAPTER_PATTERN.test(verse);
  const chapterNum = inputsValid ? parseInt(chapter, 10) : 0;
  const verseNum = inputsValid ? parseInt(verse, 10) : 0;

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [retryTick, setRetryTick] = useState(0);

  const frozenAnchor = useMemo(() => {
    if (!inputsValid) return null;
    const note = notesCoveringVerse(
      sources.bookmarks,
      bookSlug,
      chapterNum,
      verseNum,
    )[0];
    return note ? singleVerseFromFrozenAnchor(note.anchor, verseNum) : null;
  }, [sources.bookmarks, inputsValid, bookSlug, chapterNum, verseNum]);

  // Keep the passage context already offered to this editor even if a sync
  // removes its last saved source note. This retains no note and restores none.
  // An exact context may replace an ambiguous one, never the reverse. The
  // passage key and existing owner gate still reset this route-local state.
  const [retainedFallback, setRetainedFallback] =
    useState<ReturnType<typeof singleVerseFromFrozenAnchor>>(null);
  if (frozenAnchor && (!retainedFallback || (!retainedFallback.exact && frozenAnchor.exact))) {
    setRetainedFallback(frozenAnchor);
  }
  const fallback = retainedFallback ?? frozenAnchor;

  // Bible availability belongs to this passage, not to the bookmark list.
  // Keep a usable editor mounted during a retry as well as an ordinary refresh.
  // The keyed passage starts in loading; a late load cannot enter another page.
  useEffect(() => {
    if (!inputsValid || !sourcesReady) return;
    let cancelled = false;
    ensureBookAvailable(bookSlug)
      .then((book) => {
        if (cancelled) return;
        const blocks = book.chapters[String(chapterNum)] ?? null;
        const built = blocks
          ? buildScriptureAnchor({
              bookSlug,
              chapter: chapterNum,
              blocks,
              selection: { kind: 'verses', verses: [verseNum] },
            })
          : null;
        setState(built ? { kind: 'ready', anchor: built } : { kind: 'missing' });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[reflection] anchor build failed:', err);
        setState({
          kind: 'failed',
          offline: typeof navigator !== 'undefined' && !navigator.onLine,
        });
      });
    ensureBibleHydrated().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [inputsValid, sourcesReady, bookSlug, chapterNum, verseNum, retryTick]);

  // Saved provenance remains available when the requested Bible book is not.
  // A wider, ambiguous frozen passage is readable but cannot anchor a new entry.
  const anchor = state.kind === 'ready' ? state.anchor : fallback?.anchor ?? null;
  const allowCreate = state.kind === 'ready' || fallback?.exact === true;
  const loadFailed = state.kind === 'failed' ||
    (state.kind === 'missing' && fallback !== null && !fallback.exact);
  const offline = state.kind === 'failed'
    ? state.offline
    : typeof navigator !== 'undefined' && !navigator.onLine;

  if (!BIBLE_BOOKMARKS_ENABLED) return <Navigate to="/bible" replace />;
  // Bad params or a degenerate verse (past chapter end / omitted-only) -> back
  // to the journal (§9).
  if (!inputsValid || (state.kind === 'missing' && !fallback))
    return <Navigate to="/bible/bookmarks" replace />;

  // Parent is the passage page (canon-spatial redesign Phase 1 §6 P1); native
  // back still covers the actual entry path. If deleting the last note empties
  // the chapter, the passage page's empty-chapterMarks guard redirects up.
  const passagePath = `/bible/bookmarks/book/${bookSlug}/${chapterNum}`;

  return (
    <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
      <div className={styles.readerBg} aria-hidden="true" />
      <div className={styles.container} data-bible-theme={bibleTheme}>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />
      <div className={styles.topBar}>
        <Link to={passagePath} className={styles.backLink}>
          {t('bible.back')}
        </Link>
        <div className={styles.headerActions}>
          <BibleThemePicker />
        </div>
      </div>

      {!sourcesReady ? (
        <JournalSourceState state={sourceState} onRetry={retrySources} />
      ) : state.kind === 'loading' ? (
        <p className={styles.status}>{t('bible.chapterLoading')}</p>
      ) : loadFailed && !anchor ? (
        <button
          type="button"
          className={styles.retryNotice}
          onClick={() => setRetryTick((n) => n + 1)}
        >
          {t(
            offline
              ? 'bible.offlineNeedsDownload'
              : 'bible.chapterReaderLoadFailed',
          )}
        </button>
      ) : anchor ? (
        <>
          {loadFailed && (
            <button
              type="button"
              className={styles.retryNotice}
              onClick={() => setRetryTick((n) => n + 1)}
            >
              {t(
                offline
                  ? 'bible.offlineNeedsDownload'
                  : 'bible.chapterReaderLoadFailed',
              )}
            </button>
          )}
          <h1 className={styles.reference}>{anchor.reference}</h1>
          <p className={styles.verseText}>{anchor.text}</p>
          <Link
            to={`/bible/${bookSlug}/${chapterNum}?v=${verseNum}`}
            className={styles.readInContext}
          >
            {t('bookmark.readInContext')}
          </Link>
          <div className={styles.divider} />
          <BibleBookmarkSheet
            presentation="page"
            mode="thread"
            anchor={anchor}
            threadVerse={verseNum}
            allowCreate={allowCreate}
            voiceTarget={`bookmark-reflection-${bookSlug}-${chapterNum}-${verseNum}`}
            onClose={() => navigate(passagePath, { replace: true })}
          />
        </>
      ) : (
        <p className={styles.status}>{t('bible.chapterLoading')}</p>
      )}
      </div>
    </div>
  );
}
